import { Entity } from '@backstage/catalog-model';
import { isSkyStageAdmin } from './skystageAdmins';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

/**
 * Check if the current user is authorized to manage (delete/unregister) this entity.
 * User is authorized if they are in the owners list, are the original creator, or
 * are a SkyStage platform administrator (who get owner-level access to every
 * microapp — see ./skystageAdmins).
 * Both microapps and MCP servers use microapp/owners and microapp/createdBy annotations.
 *
 * @param entity - The catalog entity to check authorization for
 * @param currentUserEmail - The email of the current user
 * @param adminEmails - SkyStage admin allowlist (from useSkyStageAdminEmails);
 *   defaults to empty so existing callers stay behavior-compatible.
 * @returns true if the user is authorized to manage the entity
 */
export function isUserAuthorizedForEntity(
  entity: Entity | undefined,
  currentUserEmail: string,
  adminEmails: readonly string[] = [],
): boolean {
  if (!currentUserEmail || !entity?.metadata) return false;

  const isMicroappOrMcp =
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
      MICROAPP_ANNOTATION_VALUE ||
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] === 'mcp-server' ||
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
      'public-microapp';
  if (!isMicroappOrMcp) return false;

  // SkyStage admins get owner-level access to every microapp/MCP server.
  if (isSkyStageAdmin(currentUserEmail, adminEmails)) return true;

  const appOwners = entity.metadata.annotations?.['microapp/owners'] || '';
  const appCreatedBy =
    entity.metadata.annotations?.['microapp/createdBy'] || '';

  const ownersList = appOwners
    ? appOwners.split(',').map(email => email.trim().toLowerCase())
    : [];

  const emailLower = currentUserEmail.toLowerCase();
  return (
    ownersList.includes(emailLower) ||
    (!!appCreatedBy && appCreatedBy.toLowerCase() === emailLower)
  );
}

/**
 * Check if the current user is allowed to ACCESS a Custom visibility microapp.
 *
 * This is different from isUserAuthorizedForEntity (which checks owner/management rights).
 * Access is granted if:
 *  1. The app does NOT have "Custom (Specific Users)" visibility (all users can access).
 *  2. The user is an owner (in microapp/owners annotation).
 *  3. The user is the original creator (microapp/createdBy annotation).
 *  4. The user email is in the microapp/allowedUsers annotation.
 *
 * @param entity - The catalog entity to check access for
 * @param currentUserEmail - The email of the current user
 * @param adminEmails - SkyStage admin allowlist (from useSkyStageAdminEmails);
 *   defaults to empty so existing callers stay behavior-compatible.
 * @returns true if the user is allowed to access the entity
 */
export function isUserAllowedToAccessEntity(
  entity: Entity | undefined,
  currentUserEmail: string,
  adminEmails: readonly string[] = [],
): boolean {
  if (!entity?.metadata) return false;

  const visibility = entity.metadata.annotations?.['microapp/visibility'] || '';

  // Only "Custom (Specific Users)" visibility has restricted access
  if (visibility !== 'Custom (Specific Users)') return true;

  if (!currentUserEmail) return false;
  const emailLower = currentUserEmail.toLowerCase();

  // SkyStage admins can always access (owner-level access everywhere).
  if (isSkyStageAdmin(currentUserEmail, adminEmails)) return true;

  // Owners always have access
  const appOwners = entity.metadata.annotations?.['microapp/owners'] || '';
  const ownersList = appOwners
    ? appOwners.split(',').map((email: string) => email.trim().toLowerCase())
    : [];
  if (ownersList.includes(emailLower)) return true;

  // Original creator always has access
  const createdBy =
    entity.metadata.annotations?.['microapp/createdBy']?.toLowerCase() || '';
  if (createdBy && createdBy === emailLower) return true;

  // Check allowedUsers annotation
  const allowedUsersRaw =
    entity.metadata.annotations?.['microapp/allowedUsers'] || '';
  const allowedUsers = allowedUsersRaw
    ? allowedUsersRaw
        .split(',')
        .map((email: string) => email.trim().toLowerCase())
        .filter(Boolean)
    : [];
  return allowedUsers.includes(emailLower);
}
